import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Snackbar, Chip, Stack
} from '@mui/material';
import { Add, Delete, Refresh, PersonAdd } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function UsuariosCajeros() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', password: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/cajero/listar`, { headers: headers() });
      setUsuarios(res.data?.cajeros || res.data || []);
    } catch { setUsuarios([]); }
    setLoading(false);
  };

  const crearCajero = async () => {
    if (!form.email || !form.password) {
      return setSnackbar({ open: true, message: 'Email y contraseña son requeridos', severity: 'error' });
    }
    try {
      await axios.post(`${API_URL}/api/cajero/crear`, {
        nombre: form.nombre || form.email.split('@')[0],
        email: form.email,
        password: form.password,
      }, { headers: headers() });
      setSnackbar({ open: true, message: 'Cajero/Vendedor creado exitosamente', severity: 'success' });
      setDialogOpen(false);
      setForm({ nombre: '', email: '', password: '' });
      cargar();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al crear', severity: 'error' });
    }
  };

  const eliminarCajero = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await axios.delete(`${API_URL}/api/cajero/${id}`, { headers: headers() });
      setSnackbar({ open: true, message: 'Usuario eliminado', severity: 'success' });
      cargar();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>👥 Usuarios / Cajeros</Typography>
            <Typography variant="body2" color="text.secondary">
              Cree vendedores que podrán usar el POS y emitir facturas con su firma electrónica.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargar}>Actualizar</Button>
            <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setDialogOpen(true)}>Nuevo Cajero</Button>
          </Stack>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Los cajeros/vendedores pueden acceder al POS, crear facturas y ver el listado de facturas.
          Usan la firma electrónica de su cuenta de administrador para emitir al SRI.
        </Alert>

        {loading ? <Typography>Cargando...</Typography> : usuarios.length === 0 ? (
          <Alert severity="warning">No hay cajeros registrados. Cree uno para que pueda usar el POS.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Rol</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.nombre || u.email?.split('@')[0]}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Chip label={u.role || 'CAJERO'} size="small" color="primary" /></TableCell>
                    <TableCell align="center">
                      <Chip label={u.activo !== false ? 'Activo' : 'Inactivo'} size="small"
                        color={u.activo !== false ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => eliminarCajero(u.id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog crear cajero */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>➕ Nuevo Cajero / Vendedor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField fullWidth label="Nombre" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Juan Pérez" />
            <TextField fullWidth required label="Email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cajero@empresa.com" />
            <TextField fullWidth required label="Contraseña" type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres"
              helperText="El cajero usará este email y contraseña para iniciar sesión" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crearCajero}>Crear Cajero</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
